import dayjs from 'dayjs'

const TAG_SEPARATOR = ' · '

// Stored dates can carry "T00:00:00.000Z"; read as an instant they land on the previous day behind UTC.
export const holidayDay = (date: string): string => date.slice(0, 10)

export const holidayLine = (name: string, locationNames: string[] | null): string => (locationNames?.length ? `${name}${TAG_SEPARATOR}${locationNames.join(', ')}` : name)

export interface IWallchartHolidayScreen {
  leaves: { user: { locationId: string } }[]
  locations: { id: string; holidays: { holidays: { date: string }[] }[] }[]
  dateFrom: Date
  dateTo: Date
}

// A holiday is drawn only on rows of its own location's users, so a location with nobody on screen contributes nothing.
export function locationsOnScreen<T extends { id: string }>(leaves: IWallchartHolidayScreen['leaves'], locations: T[]): T[] {
  const onScreen = new Set(leaves.map((row) => row.user.locationId))
  return locations.filter((location) => onScreen.has(location.id))
}

export function wallchartHolidayLocations({ leaves, locations, dateFrom, dateTo }: IWallchartHolidayScreen): Set<string> {
  const from = dayjs(dateFrom).format('YYYY-MM-DD')
  const to = dayjs(dateTo).format('YYYY-MM-DD')
  return new Set(
    locationsOnScreen(leaves, locations)
      .filter((location) =>
        location.holidays.some((year) =>
          year.holidays.some((holiday) => {
            const date = holidayDay(holiday.date)
            return date >= from && date <= to
          })
        )
      )
      .map((location) => location.id)
  )
}

export interface IHolidayTagSource {
  date: string
  name: string
  locationId: string
  locationName: string
}

export interface IMonthOnScreen {
  year: number
  month: number
}

export interface IHolidayPeople {
  byLocation: Record<string, number>
  format: (count: number) => string
}

const peopleIn = (locationIds: string[], byLocation: Record<string, number>): number => [...new Set(locationIds)].reduce((sum, id) => sum + (byLocation[id] ?? 0), 0)

export function teamCalendarHolidayLines(holidays: IHolidayTagSource[], { year, month }: IMonthOnScreen, people?: IHolidayPeople): Record<string, string[]> {
  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}-`
  const inMonth = holidays.filter((holiday) => holidayDay(holiday.date).startsWith(monthPrefix))
  const tagged = new Set(inMonth.map((holiday) => holiday.locationId)).size > 1

  const byDate = new Map<string, Map<string, { places: string[]; locationIds: string[] }>>()
  inMonth.forEach((holiday) => {
    const day = holidayDay(holiday.date)
    const names = byDate.get(day) ?? new Map<string, { places: string[]; locationIds: string[] }>()
    const entry = names.get(holiday.name) ?? { places: [], locationIds: [] }
    if (!entry.places.includes(holiday.locationName)) entry.places.push(holiday.locationName)
    entry.locationIds.push(holiday.locationId)
    names.set(holiday.name, entry)
    byDate.set(day, names)
  })

  const lines: Record<string, string[]> = {}
  byDate.forEach((names, date) => {
    lines[date] = [...names].map(([name, { places, locationIds }]) => {
      const line = holidayLine(name, tagged ? places : null)
      const count = people ? peopleIn(locationIds, people.byLocation) : 0
      return people && count > 0 ? `${line}${TAG_SEPARATOR}${people.format(count)}` : line
    })
  })
  return lines
}

export function teamCalendarHolidayPeople(holidays: IHolidayTagSource[], byLocation: Record<string, number>): Record<string, number> {
  const locationsByDate = new Map<string, string[]>()
  holidays.forEach((holiday) => {
    const day = holidayDay(holiday.date)
    const locationIds = locationsByDate.get(day) ?? []
    locationIds.push(holiday.locationId)
    locationsByDate.set(day, locationIds)
  })
  return Object.fromEntries([...locationsByDate].map(([date, locationIds]) => [date, peopleIn(locationIds, byLocation)]))
}
